import {
  formatAreaSqM,
  formatBooleanValue,
  formatDetailDate,
  formatDetailDateTime,
  formatDetailNumber,
  formatListingTypeLabel,
  formatPropertyLabel,
} from "./propertyDetailFormat";

function hasValue(value) {
  if (value == null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return true;
}

export const CORE_FACT_DEFINITIONS = [
  {
    key: "bedrooms",
    label: "Bedrooms",
    group: "Core Facts",
    icon: "bed",
    getValue: (p) => p.bedrooms,
    format: (v) => formatDetailNumber(v),
  },
  {
    key: "bathrooms",
    label: "Bathrooms",
    group: "Core Facts",
    icon: "bath",
    getValue: (p) => p.bathrooms,
    format: (v) => formatDetailNumber(v),
  },
  {
    key: "parkingSpaces",
    label: "Parking",
    group: "Core Facts",
    icon: "parking",
    getValue: (p) => p.parkingSpaces,
    format: (v) => formatDetailNumber(v, "spaces"),
  },
  {
    key: "areaM2",
    label: "Area",
    group: "Core Facts",
    icon: "area",
    getValue: (p) => p.areaM2 ?? p.builtAreaM2,
    format: (v) => formatAreaSqM(v),
  },
  {
    key: "builtAreaM2",
    label: "Built area",
    group: "Core Facts",
    icon: "built",
    getValue: (p) => (p.builtAreaM2 !== p.areaM2 ? p.builtAreaM2 : null),
    format: (v) => formatAreaSqM(v),
  },
  {
    key: "lotAreaM2",
    label: "Lot area",
    group: "Core Facts",
    icon: "lot",
    getValue: (p) => p.lotAreaM2,
    format: (v) => formatAreaSqM(v),
  },
  {
    key: "propertyType",
    label: "Property type",
    group: "Core Facts",
    icon: "type",
    getValue: (p) => p.propertyType,
    format: (v) => formatPropertyLabel(v),
  },
  {
    key: "listingType",
    label: "Listing type",
    group: "Core Facts",
    icon: "listing",
    getValue: (p) => p.listingType || p.transactionType,
    format: (v) => formatListingTypeLabel(v),
  },
  {
    key: "administrationFee",
    label: "Administration / HOA",
    group: "Core Facts",
    icon: "fee",
    getValue: (p) => p.administrationFee,
    format: (v, p) =>
      v != null
        ? new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: p.currency || "COP",
            maximumFractionDigits: 0,
          }).format(v)
        : null,
  },
];

export const LOCATION_DETAIL_DEFINITIONS = [
  {
    key: "displayAddress",
    label: "Address",
    group: "Location",
    getValue: (p) => p.location?.displayAddress || p.addressText,
    format: (v) => v,
  },
  {
    key: "neighborhood",
    label: "Neighborhood",
    group: "Location",
    getValue: (p) => p.location?.neighborhood || p.neighborhood,
    format: (v) => v,
  },
  {
    key: "municipality",
    label: "Municipality",
    group: "Location",
    getValue: (p) => p.location?.municipality || p.municipality,
    format: (v) => v,
  },
  {
    key: "department",
    label: "Department",
    group: "Location",
    getValue: (p) => p.location?.department || p.department,
    format: (v) => v,
  },
  {
    key: "locationQuality",
    label: "Location quality",
    group: "Location",
    getValue: (p) => p.location?.locationQuality || p.locationQuality,
    format: (v) => formatPropertyLabel(v),
  },
  {
    key: "coordinateConfidence",
    label: "Coordinate confidence",
    group: "Location",
    getValue: (p) => p.location?.coordinateConfidence ?? p.coordinateConfidence,
    format: (v) => (v != null ? `${Math.round(Number(v) * 100)}%` : null),
  },
];

export function collectAttributeValues(property = {}, definitions = []) {
  return definitions
    .map((definition) => {
      const raw = definition.getValue(property);
      if (!hasValue(raw)) {
        return null;
      }

      const formatted = definition.format(raw, property);
      if (!hasValue(formatted)) {
        return null;
      }

      return {
        ...definition,
        raw,
        formatted: Array.isArray(formatted) ? formatted : String(formatted),
        values: Array.isArray(formatted) ? formatted : null,
      };
    })
    .filter(Boolean);
}

export function groupAttributes(items = []) {
  return items.reduce((groups, item) => {
    const groupName = item.group || "Details";
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(item);
    return groups;
  }, {});
}

export { hasValue, formatBooleanValue, formatDetailDate, formatDetailDateTime };
